import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Star, Award } from 'lucide-react';

const testimonials = [
  {
    quote: "ProfitIQ.AI's AI-driven insights helped us identify $127,000 in operational inefficiencies within the first quarter. Their strategic guidance transformed our manufacturing processes and led to a 23% increase in production efficiency. The predictive maintenance alerts alone prevented two major equipment failures.",
    author: "Carlos Mendez",
    role: "Operations Director",
    company: "SummitTech Manufacturing",
    industry: "Manufacturing",
    metrics: {
      efficiency: "+23%",
      savings: "$127K",
      timeframe: "3 months"
    },
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=200&h=200",
    awards: ["Industry Leader 2024", "Top AI Implementation"]
  },
  {
    quote: "Before ProfitIQ.AI, we were losing customers without understanding why. Their AI analytics revealed critical engagement gaps in our patient follow-up process, helping us improve retention by 32% and boost recurring revenue by $89,000 annually. The automated patient insights have revolutionized our practice.",
    author: "Dr. Sarah Chen",
    role: "CEO",
    company: "MedTech Innovations",
    industry: "Healthcare",
    metrics: {
      retention: "+32%",
      revenue: "+$89K",
      timeframe: "12 months"
    },
    image: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?auto=format&fit=crop&w=200&h=200",
    awards: ["Healthcare Innovation Award", "Digital Excellence"]
  },
  {
    quote: "The AI-powered financial forecasting has been a game-changer for our logistics operations. We've optimized our cash flow management and reduced operational costs by 18%. The real-time route optimization and demand forecasting helped us save $234,000 in fuel costs alone. ProfitIQ.AI paid for itself within the first month.",
    author: "Marcus Thompson",
    role: "CFO",
    company: "Velocity Logistics",
    industry: "Transportation",
    metrics: {
      costs: "-18%",
      savings: "$234K",
      timeframe: "1 month"
    },
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=200&h=200",
    awards: ["Logistics Excellence", "Sustainability Leader"]
  }
];

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

const stats = [
  { value: '500+', label: 'Businesses Transformed' },
  { value: '$50M+', label: 'Client Cost Savings' },
  { value: '98%', label: 'Client Success Rate' },
  { value: '4.9/5', label: 'Client Satisfaction' }
];

export const Testimonials = () => {
  return (
    <section className="relative py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ...springConfig }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/10 text-amber-400 mb-4">
            <Award className="w-4 h-4" />
            <span className="text-sm font-medium">Award-Winning AI Platform</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
              Real Results, Real Businesses
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            See how businesses are transforming their operations with AI-powered insights
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 1, ...springConfig }}
                className="p-4 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10"
              >
                <div className="text-2xl font-bold text-amber-400 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
            ))}
            <span className="ml-2 text-amber-400 font-semibold">4.8/5</span>
            <span className="ml-1 text-gray-400">from 127 verified reviews</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 1, ...springConfig }}
            >
              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ duration: 0.8, ...springConfig }}
                className="h-full p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm group"
              >
                <Quote className="w-8 h-8 text-amber-400 mb-4" />
                
                <div className="flex items-center gap-2 mb-4">
                  <div className="text-xs text-amber-400 font-medium px-2 py-1 rounded-full bg-amber-400/10">
                    {testimonial.industry} Industry
                  </div>
                  {testimonial.awards.map((award, i) => (
                    <div key={i} className="text-xs text-amber-400 font-medium px-2 py-1 rounded-full bg-amber-400/10 flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {award}
                    </div>
                  ))}
                </div>

                <p className="text-gray-300 mb-6 italic">{testimonial.quote}</p>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  {Object.entries(testimonial.metrics).map(([key, value]) => (
                    <div key={key} className="text-center p-2 rounded-lg bg-amber-400/5">
                      <div className="text-lg font-bold text-amber-400">{value}</div>
                      <div className="text-xs text-gray-400 capitalize">{key}</div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full mr-4 object-cover"
                  />
                  <div>
                    <h4 className="text-white font-semibold">{testimonial.author}</h4>
                    <p className="text-gray-400 text-sm">{testimonial.role}</p>
                    <p className="text-amber-400 text-sm">{testimonial.company}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};